import React from "react"

import isEqual from "lodash/isEqual"
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil"

import { diff } from "~/utils/diff"

import type { Design } from "./design-model"
import { designLocalState, designRemoteState } from "./design-state"

const alwaysKeepLocalFields: (keyof Design)[] = [
  `inverter`,
  `inverterId`,
  `secondaryInverter`,
  `secondaryInverterId`,
]

export const useDesignOptimisticUpdate = (
  localRef: React.MutableRefObject<Design>,
): void => {
  const getLocalDesign = useRecoilCallback(({ snapshot }) => () => {
    const value = snapshot.getLoadable(designLocalState).contents

    return value
  })
  const setLocal = useSetRecoilState(designLocalState)
  const remote = useRecoilValue(designRemoteState)

  const remoteRef = React.useRef(remote)

  React.useEffect(() => {
    if (isEqual(remote, remoteRef.current)) {
      return
    }

    const previousLocalDesign = getLocalDesign()
    setLocal(remote)
    remoteRef.current = remote

    if (localRef.current.id !== ``) {
      const localDiff = diff(localRef.current, previousLocalDesign) ?? {}
      const fieldsToUpdate = Object.keys(localDiff).reduce((acc, field) => {
        const key = field as keyof Design
        if (alwaysKeepLocalFields.includes(key)) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - type is too complex for typescript
          acc[key] = localDiff[key]
        }

        return acc
      }, {} as Partial<Design>)
      setLocal((currentLocalDesign) => ({
        ...currentLocalDesign,
        ...fieldsToUpdate,
      }))

      return
    }
  }, [remote, setLocal, getLocalDesign, localRef])
}
